<template>
  <div>
    <b-row class="mt-2">
      <b-col cols="10" class="mb-2">
        <h5 class="mb-0">IV therapies</h5>
        <small class="text-muted">
          Add one card per IV serum. Extra vitamins are priced by facility.
        </small>
      </b-col>
      <b-col>
        <h3 class="d-flex justify-content-end">$ {{ displayTotal }}</h3>
      </b-col>
    </b-row>

    <div v-if="loadingSerums" class="text-muted small mb-3">Loading IV serums…</div>
    <b-alert v-else-if="!serumOptions.length && !cards.length" show variant="secondary" class="py-2">
      No IV serums are available. Create a package that includes IV therapies to use this step.
    </b-alert>
    <template v-else>
      <IvSerumCard
        v-for="(card, index) in cards"
        :key="card._key"
        :card="card"
        :index="index"
        :serum-options="serumOptions"
        :facility-addons="facilityAddons"
        :loading-addons="loadingAddons"
        :line="lineFor(card)"
        @change="value => updateCard(index, value)"
        @remove="removeCard(index)" />

      <b-alert v-if="invalidMessage && cards.length" show variant="warning" class="py-2">
        {{ invalidMessage }}
      </b-alert>
      <b-alert v-else-if="quoteError" show variant="warning" class="py-2">{{ quoteError }}</b-alert>
      <div v-else-if="quoting" class="text-muted small mb-2">Calculating facility price…</div>

      <div class="d-flex justify-content-between align-items-center">
        <b-button variant="outline-primary" size="sm" @click="addCard">
          <i class="fas fa-plus mr-1"></i> Add IV serum
        </b-button>
        <strong v-if="pricing">IV total: ${{ pricing.iv_total }} {{ pricing.currency || 'USD' }}</strong>
      </div>
    </template>
  </div>
</template>

<script>
import { BRow, BCol, BAlert, BButton } from 'bootstrap-vue'
import axiosPackages from '@/core/services/api/admin/consults/packages'
import axiosPackagesInsurance from '@/core/services/apiInsurance/admin/consults/packages'
import ivApi from '@/core/services/api/admin/iv'
import { isIvTherapyPackage, parseIntegerQty, resolveIvAddonsForFacility } from '@/core/utils/ivTherapies'
import { toProjectDateTime } from '@/core/services/api/weightloss-inventory/dateFormat'
import IvSerumCard from './IvSerumCard'

let cardKey = 0

/**
 * Paso 7 del OMA (regular e insurance): N tarjetas de suero, cada una con sus vitaminas.
 * Cotiza todo con `POST iv/pricing/v2/quote` y emite `change` con el payload `iv.serums` y los totales.
 */
export default {
  name: 'IvTherapiesProcedure',
  components: { BRow, BCol, BAlert, BButton, IvSerumCard },
  props: {
    initialSerums: { type: Array, default: () => [] },
    facilityId: { type: [Number, String], default: null },
    visitDate: { type: [String, Date], default: null },
    packagesLane: { type: String, default: 'regular' },
  },
  data() {
    return {
      cards: [],
      serumOptions: [],
      loadingSerums: false,
      facilityAddons: [],
      loadingAddons: false,
      pricing: null,
      quotedKeys: [],
      quoting: false,
      quoteError: '',
      quoteTimer: null,
      quoteSeq: 0,
    }
  },
  computed: {
    packageApi() {
      return this.packagesLane === 'insurance' ? axiosPackagesInsurance : axiosPackages
    },
    displayTotal() {
      return this.pricing ? this.pricing.iv_total : '0.00'
    },
    invalidMessage() {
      if (this.cards.some(card => !card.package_id)) {
        return 'Select an IV serum on every card or remove the empty ones.'
      }
      const rows = this.cards.flatMap(card => card.addons.filter(row => row.iv_addon_id))
      if (rows.some(row => parseIntegerQty(row.qty) == null)) {
        return 'Vitamin quantity must be a whole number greater than or equal to 1.'
      }
      const duplicated = this.cards.some(card => {
        const ids = card.addons.filter(row => row.iv_addon_id).map(row => Number(row.iv_addon_id))
        return new Set(ids).size !== ids.length
      })
      return duplicated ? 'The same vitamin cannot be repeated within the same serum.' : ''
    },
    serumsPayload() {
      return this.cards
        .filter(card => card.package_id)
        .map(card => ({
          package_id: Number(card.package_id),
          addons: card.addons
            .filter(row => row.iv_addon_id)
            .map(row => ({ iv_addon_id: Number(row.iv_addon_id), qty: parseIntegerQty(row.qty) })),
        }))
    },
  },
  watch: {
    initialSerums: {
      immediate: true,
      handler(lines) {
        if (lines && lines.length) this.hydrate(lines)
      },
    },
    facilityId() {
      this.loadFacilityAddons()
      this.scheduleQuote()
    },
    visitDate() {
      this.loadFacilityAddons()
      this.scheduleQuote()
    },
  },
  created() {
    this.loadSerums()
    this.loadFacilityAddons()
  },
  beforeDestroy() {
    if (this.quoteTimer) clearTimeout(this.quoteTimer)
  },
  methods: {
    hydrate(lines) {
      const packages = []
      this.cards = lines.map(line => {
        if (line.package) packages.push(line.package)
        return {
          _key: `iv-card-${++cardKey}`,
          package_id: line.package ? line.package.id : null,
          addons: (line.addons || []).map(addon => ({
            _key: `iv-card-addon-${addon.iv_visit_addon_id || ++cardKey}`,
            iv_addon_id: Number(addon.iv_addon_id),
            name: addon.name,
            qty: parseInt(addon.qty, 10) || 1,
          })),
        }
      })
      this.mergeSerumOptions(packages)
      this.scheduleQuote()
    },
    mergeSerumOptions(packages) {
      const known = new Set(this.serumOptions.map(item => Number(item.id)))
      const missing = packages.filter(pkg => pkg && !known.has(Number(pkg.id)))
      if (missing.length) this.serumOptions = [...this.serumOptions, ...missing]
    },
    lineFor(card) {
      const index = this.quotedKeys.indexOf(card._key)
      return index >= 0 && this.pricing ? this.pricing.serums[index] || null : null
    },
    addCard() {
      this.cards.push({ _key: `iv-card-${++cardKey}`, package_id: null, addons: [] })
      this.scheduleQuote()
    },
    removeCard(index) {
      this.cards.splice(index, 1)
      this.scheduleQuote()
    },
    updateCard(index, card) {
      this.$set(this.cards, index, card)
      this.scheduleQuote()
    },
    async loadSerums() {
      this.loadingSerums = true
      try {
        const { data: { data } } = await this.packageApi.packageFilter(100, { name: '', status: true })
        const serums = (data || []).filter(isIvTherapyPackage)
        const hydrated = this.serumOptions.filter(
          item => !serums.some(serum => Number(serum.id) === Number(item.id))
        )
        this.serumOptions = [...serums, ...hydrated]
      } catch (e) {
        this.serumOptions = []
      } finally {
        this.loadingSerums = false
      }
    },
    async loadFacilityAddons() {
      this.loadingAddons = true
      try {
        const [prices, addonResult] = await Promise.all([
          ivApi.listAllPrices(),
          ivApi.listAddons(200, 1).catch(() => ({ items: [] })),
        ])
        this.facilityAddons = resolveIvAddonsForFacility(
          prices,
          addonResult.items || [],
          this.facilityId,
          this.visitDate
        )
      } catch (e) {
        this.facilityAddons = []
      } finally {
        this.loadingAddons = false
      }
    },
    scheduleQuote() {
      if (this.quoteTimer) clearTimeout(this.quoteTimer)
      this.quoting = this.serumsPayload.length > 0 && !this.invalidMessage
      this.emitChange()
      this.quoteTimer = setTimeout(() => this.fetchQuote(), 400)
    },
    async fetchQuote() {
      const seq = ++this.quoteSeq
      const serums = this.serumsPayload
      if (!serums.length || this.invalidMessage) {
        this.pricing = null
        this.quotedKeys = []
        this.quoteError = ''
        this.quoting = false
        this.emitChange()
        return
      }
      const keys = this.cards.filter(card => card.package_id).map(card => card._key)
      this.quoting = true
      this.quoteError = ''
      try {
        const pricing = await ivApi.quoteSerums({
          facilityId: this.facilityId,
          date: toProjectDateTime(this.visitDate) || toProjectDateTime(new Date()),
          serums,
        })
        if (seq !== this.quoteSeq) return
        this.pricing = pricing
        this.quotedKeys = keys
      } catch (e) {
        if (seq !== this.quoteSeq) return
        this.pricing = null
        this.quotedKeys = []
        this.quoteError = e.message || 'Could not quote IV serums'
      } finally {
        if (seq === this.quoteSeq) {
          this.quoting = false
          this.emitChange()
        }
      }
    },
    emitChange() {
      const pricing = this.pricing
      this.$emit('change', {
        serums: this.serumsPayload,
        pricing,
        serumsTotal: pricing ? Number(pricing.serums_total) || 0 : 0,
        addonsTotal: pricing ? Number(pricing.addons_total) || 0 : 0,
        ivTotal: pricing ? Number(pricing.iv_total) || 0 : 0,
        error: this.quoteError,
        loading: this.quoting,
        invalid: this.cards.length ? this.invalidMessage : '',
      })
    },
  },
}
</script>
